import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["moduleId", "as", "verticalPositioning", "popoverPlacement", "children", "collaborationSidebarState", "onUpdateCollaborationSidebarState", "showIndicator", "showIndicatorsOnHover", "iconPosition", "iconProps", "renderCommentIndicator", "metaDescription", "userInfo", "embeddedPopoverAnchorSelector"];
import { c as _c } from "react-compiler-runtime";
import { useState, useEffect, useCallback, useMemo } from 'react';
import classnames from 'classnames';
import styled from 'styled-components';
import useEmbeddedComments from 'collaboration-sidebar/features/deepCommenting/hooks/useEmbeddedComments';
import useButtonA11yProps from 'collaboration-sidebar/hooks/useButtonA11yProps';
import useCommentableAreaStyles from 'collaboration-sidebar/features/deepCommenting/hooks/useCommentableAreaStyles';
import useUpdateMetaDescriptions from 'collaboration-sidebar/features/deepCommenting/hooks/useUpdateMetaDescriptions';
import { COMMENTABLE_AREA_IDENTIFIER } from 'collaboration-sidebar/constants/identifiers';
import EmbeddedComment from './EmbeddedComment';
import { useToggleOpenPopover } from '../hooks/useToggleOpenPopover';
import { useEmbeddedCommentHandlers } from '../hooks/useEmbeddedCommentHandlers';
import useElementResizeObserver from '../../../hooks/useElementResizeObserver';
import useWindowScrollListener from '../../../hooks/useWindowScrollListener';
import { useEmbeddedCommentChangeHandlers } from '../hooks/useEmbeddedCommentChangeHandlers';
import { useEmbeddedCommentClickListener } from '../hooks/useEmbeddedCommentClickListener';
import usePrevious from '../../../hooks/usePrevious';
import useEmbeddedCommentsNavigation from '../hooks/useEmbeddedCommentsNavigation';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TD_ELEMENT_TYPE = 'TD';
const StyledHoverableCommentableArea = styled.div.withConfig({
  displayName: "EmbeddedCommentableArea__StyledHoverableCommentableArea"
})(["outline-color:rgba(255,122,89,0) !important;position:relative !important;", " ", " ", " ", " ", " ", " ", ""], ({
  outlineRadius
}) => outlineRadius && `border-radius: ${outlineRadius}px;`, ({
  showComments,
  isCommentModeActive
}) => (showComments || isCommentModeActive) && `
    transition: outline-color 0.15s ease-in, box-shadow 0.15s ease-in;
    outline: 2px solid rgba(255, 122, 89, 0) !important;
    pointer-events: auto !important;

    > td {
      pointer-events: auto !important;
    }
  `, ({
  isHighlightedBySidebar,
  outlineRadius
}) => isHighlightedBySidebar && (outlineRadius ? 'box-shadow: 0 0 0 2px rgb(255 122 89);' : 'outline-color: rgba(255, 122, 89, 1) !important;'), ({
  isTableChild,
  isDefaultElement
}) => isTableChild && isDefaultElement && `
    outline-offset: -2px;
    display: table;
    width: 100%;
  `, ({
  isTr
}) => isTr && `
    outline-offset: -2px;
  `, ({
  isCommentModeActive,
  outlineRadius
}) => isCommentModeActive && `
    &:hover, &:focus {
      cursor: crosshair;

      ${outlineRadius ? 'box-shadow: 0 0 0 2px rgb(255 122 89);' : 'outline-color: rgba(255, 122, 89, 1) !important;'}

      *:not(#comment-indicator):not(#comment-indicator *) {
        pointer-events: none;
      }
    }
  `, ({
  isCommentModeActive,
  isCommenting,
  outlineRadius
}) => isCommentModeActive && isCommenting && `
    ${outlineRadius ? 'box-shadow: 0 0 0 2px rgb(255 122 89);' : 'outline-color: rgba(255, 122, 89, 1) !important;'}
      &:hover {
        cursor: default;
      }
    `);
function EmbeddedCommentableArea(t0) {
  "use memo";

  const $ = _c(134);
  let children;
  let collaborationSidebarState;
  let embeddedPopoverAnchorSelector;
  let iconPosition;
  let iconProps;
  let metaDescription;
  let moduleId;
  let onUpdateCollaborationSidebarState;
  let popoverPlacement;
  let propsForWrapper;
  let renderCommentIndicator;
  let t1;
  let t2;
  let t3;
  let t4;
  let userInfo;
  if ($[0] !== t0) {
    var _t = t0;
    ({
      moduleId,
      as: t1,
      verticalPositioning: t2,
      popoverPlacement,
      children,
      collaborationSidebarState,
      onUpdateCollaborationSidebarState,
      showIndicator: t3,
      showIndicatorsOnHover: t4,
      iconPosition,
      iconProps,
      renderCommentIndicator,
      metaDescription,
      userInfo,
      embeddedPopoverAnchorSelector
    } = _t);
    propsForWrapper = _objectWithoutPropertiesLoose(_t, _excluded);
    _t;
    $[0] = t0;
    $[1] = children;
    $[2] = collaborationSidebarState;
    $[3] = embeddedPopoverAnchorSelector;
    $[4] = iconPosition;
    $[5] = iconProps;
    $[6] = metaDescription;
    $[7] = moduleId;
    $[8] = onUpdateCollaborationSidebarState;
    $[9] = popoverPlacement;
    $[10] = propsForWrapper;
    $[11] = renderCommentIndicator;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = userInfo;
  } else {
    children = $[1];
    collaborationSidebarState = $[2];
    embeddedPopoverAnchorSelector = $[3];
    iconPosition = $[4];
    iconProps = $[5];
    metaDescription = $[6];
    moduleId = $[7];
    onUpdateCollaborationSidebarState = $[8];
    popoverPlacement = $[9];
    propsForWrapper = $[10];
    renderCommentIndicator = $[11];
    t1 = $[12];
    t2 = $[13];
    t3 = $[14];
    t4 = $[15];
    userInfo = $[16];
  }
  const as = t1 === undefined ? "div" : t1;
  const verticalPositioning = t2 === undefined ? false : t2;
  const showIndicator = t3 === undefined ? true : t3;
  const showIndicatorsOnHover = t4 === undefined ? false : t4;
  const [commentableArea, setCommentableArea] = useState(null);
  const [embeddedCommentWrapper, setEmbeddedCommentWrapper] = useState(null);
  const [autoOpenCommentPopover, setAutoOpenCommentPopover] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  let t5;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setIsHovering(false);
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const onRemoveComment = t5;
  const {
    isEmbeddedPopoverOpen,
    embeddedModuleID
  } = collaborationSidebarState;
  const isOpen = isEmbeddedPopoverOpen && embeddedModuleID === moduleId;
  const prevIsOpen = usePrevious(isOpen);
  let t6;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = node => {
      setCommentableArea(node);
    };
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const commentableAreaRef = t6;
  let t7;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = node_0 => {
      setEmbeddedCommentWrapper(node_0);
    };
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const embeddedCommentWrapperRef = t7;
  let t8;
  if ($[20] !== collaborationSidebarState || $[21] !== moduleId || $[22] !== onUpdateCollaborationSidebarState) {
    t8 = {
      moduleId,
      collaborationSidebarState,
      onUpdateCollaborationSidebarState,
      onRemoveComment
    };
    $[20] = collaborationSidebarState;
    $[21] = moduleId;
    $[22] = onUpdateCollaborationSidebarState;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const {
    isCommentModeActive,
    showComments,
    isCommenting,
    setIsCommenting,
    broadcastCommentsMessage,
    moduleParentComment
  } = useEmbeddedComments(t8);
  let t9;
  if ($[24] !== commentableArea || $[25] !== moduleId) {
    t9 = {
      commentableArea,
      moduleId
    };
    $[24] = commentableArea;
    $[25] = moduleId;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const {
    isHighlightedBySidebar
  } = useEmbeddedCommentsNavigation(t9);
  const prevIsCommentModeActive = usePrevious(isCommentModeActive);
  const {
    ctaUrl,
    objectType
  } = collaborationSidebarState;
  let t10;
  if ($[27] !== children || $[28] !== commentableArea) {
    t10 = {
      commentableArea,
      children
    };
    $[27] = children;
    $[28] = commentableArea;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const {
    isTableChild,
    isTr
  } = useCommentableAreaStyles(t10);
  let t11;
  if ($[30] !== broadcastCommentsMessage || $[31] !== ctaUrl || $[32] !== metaDescription || $[33] !== moduleParentComment || $[34] !== userInfo) {
    t11 = {
      userInfo,
      metaDescription,
      moduleParentComment,
      ctaUrl,
      broadcastCommentsMessage
    };
    $[30] = broadcastCommentsMessage;
    $[31] = ctaUrl;
    $[32] = metaDescription;
    $[33] = moduleParentComment;
    $[34] = userInfo;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  useUpdateMetaDescriptions(t11);
  let t12;
  if ($[36] !== commentableArea || $[37] !== embeddedCommentWrapper || $[38] !== onUpdateCollaborationSidebarState || $[39] !== popoverPlacement || $[40] !== verticalPositioning) {
    t12 = {
      commentableArea,
      popoverPlacement,
      verticalPositioning,
      embeddedCommentWrapper,
      onUpdateCollaborationSidebarState
    };
    $[36] = commentableArea;
    $[37] = embeddedCommentWrapper;
    $[38] = onUpdateCollaborationSidebarState;
    $[39] = popoverPlacement;
    $[40] = verticalPositioning;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  const {
    updateIndicatorPlacement,
    updateEmbeddedCommentCoordinates,
    placement,
    topOffset,
    leftOffset
  } = useEmbeddedCommentHandlers(t12);
  let t13;
  if ($[42] !== collaborationSidebarState || $[43] !== embeddedCommentWrapper || $[44] !== embeddedPopoverAnchorSelector || $[45] !== metaDescription || $[46] !== moduleId || $[47] !== onUpdateCollaborationSidebarState || $[48] !== placement) {
    t13 = {
      embeddedCommentWrapper,
      onUpdateCollaborationSidebarState,
      collaborationSidebarState,
      moduleId,
      metaDescription,
      placement,
      embeddedPopoverAnchorSelector
    };
    $[42] = collaborationSidebarState;
    $[43] = embeddedCommentWrapper;
    $[44] = embeddedPopoverAnchorSelector;
    $[45] = metaDescription;
    $[46] = moduleId;
    $[47] = onUpdateCollaborationSidebarState;
    $[48] = placement;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  const toggleOpenPopover = useToggleOpenPopover(t13);
  let t14;
  if ($[50] !== updateEmbeddedCommentCoordinates || $[51] !== updateIndicatorPlacement) {
    t14 = () => {
      updateIndicatorPlacement();
      updateEmbeddedCommentCoordinates();
    };
    $[50] = updateEmbeddedCommentCoordinates;
    $[51] = updateIndicatorPlacement;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  const handleResize = t14;
  let t15;
  let t16;
  if ($[53] !== isCommenting || $[54] !== toggleOpenPopover) {
    t15 = () => {
      let autoOpenPopoverTimer;
      if (isCommenting) {
        toggleOpenPopover(true, true);
        autoOpenPopoverTimer = setTimeout(() => {
          setAutoOpenCommentPopover(true);
        }, 0);
      }
      return () => clearTimeout(autoOpenPopoverTimer);
    };
    t16 = [isCommenting, toggleOpenPopover];
    $[53] = isCommenting;
    $[54] = toggleOpenPopover;
    $[55] = t15;
    $[56] = t16;
  } else {
    t15 = $[55];
    t16 = $[56];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[57] !== isOpen || $[58] !== setIsCommenting) {
    t17 = () => {
      if (!isOpen) {
        setIsCommenting(false);
      }
      return _temp;
    };
    t18 = [isOpen, setIsCommenting];
    $[57] = isOpen;
    $[58] = setIsCommenting;
    $[59] = t17;
    $[60] = t18;
  } else {
    t17 = $[59];
    t18 = $[60];
  }
  useEffect(t17, t18);
  let t19;
  if ($[61] !== setIsCommenting || $[62] !== toggleOpenPopover) {
    t19 = () => {
      setIsCommenting(false);
      setAutoOpenCommentPopover(false);
      toggleOpenPopover(false);
    };
    $[61] = setIsCommenting;
    $[62] = toggleOpenPopover;
    $[63] = t19;
  } else {
    t19 = $[63];
  }
  const onClosePopover = t19;
  let t20;
  if ($[64] !== isCommentModeActive || $[65] !== isCommenting || $[66] !== setIsCommenting || $[67] !== showComments) {
    t20 = e => {
      if (showComments || isCommentModeActive) {
        const target = e.target instanceof HTMLElement ? e.target : null;
        const parentNode = (target === null || target === void 0 ? void 0 : target.parentNode) instanceof HTMLElement ? target.parentNode : null;
        const currentTarget = e.currentTarget instanceof HTMLElement ? e.currentTarget : null;
        if (target && currentTarget && parentNode) {
          const isActiveCommentableArea = currentTarget.id === target.id || target.nodeName === TD_ELEMENT_TYPE && parentNode.id === currentTarget.id;
          if (isCommentModeActive && !isCommenting && isActiveCommentableArea) {
            setIsCommenting(true);
          } else {
            if (isCommenting && isActiveCommentableArea) {
              e.stopPropagation();
            }
          }
        }
      }
    };
    $[64] = isCommentModeActive;
    $[65] = isCommenting;
    $[66] = setIsCommenting;
    $[67] = showComments;
    $[68] = t20;
  } else {
    t20 = $[68];
  }
  const onCommentableAreaClick = t20;
  let t21;
  bb0: {
    if (!objectType || !showIndicator) {
      t21 = false;
      break bb0;
    }
    const showIndicatorsFromHovering = showIndicatorsOnHover && (isHovering || isCommenting);
    t21 = showIndicatorsFromHovering || showComments;
  }
  const showEmbeddedComment = t21;
  let t22;
  if ($[69] !== isCommentModeActive || $[70] !== onCommentableAreaClick || $[71] !== propsForWrapper) {
    t22 = e_0 => {
      onCommentableAreaClick(e_0);
      if (!isCommentModeActive && propsForWrapper.onClick) {
        propsForWrapper.onClick(e_0);
      }
    };
    $[69] = isCommentModeActive;
    $[70] = onCommentableAreaClick;
    $[71] = propsForWrapper;
    $[72] = t22;
  } else {
    t22 = $[72];
  }
  const onClickHandler = t22;
  let t23;
  if ($[73] !== isCommentModeActive || $[74] !== onClickHandler) {
    t23 = {
      i18nKey: "collaborationSidebar.modals.commentMode.leaveComment",
      allowTabbing: isCommentModeActive,
      onClick: onClickHandler
    };
    $[73] = isCommentModeActive;
    $[74] = onClickHandler;
    $[75] = t23;
  } else {
    t23 = $[75];
  }
  const buttonA11yProps = useButtonA11yProps(t23);
  let t24;
  if ($[76] !== updateIndicatorPlacement) {
    t24 = () => {
      updateIndicatorPlacement();
    };
    $[76] = updateIndicatorPlacement;
    $[77] = t24;
  } else {
    t24 = $[77];
  }
  let t25;
  if ($[78] !== commentableArea || $[79] !== moduleId || $[80] !== updateIndicatorPlacement) {
    t25 = [moduleId, commentableArea, updateIndicatorPlacement];
    $[78] = commentableArea;
    $[79] = moduleId;
    $[80] = updateIndicatorPlacement;
    $[81] = t25;
  } else {
    t25 = $[81];
  }
  useEffect(t24, t25);
  useElementResizeObserver(commentableArea, handleResize);
  let t26;
  if ($[82] !== isOpen || $[83] !== updateEmbeddedCommentCoordinates) {
    t26 = {
      listenToScroll: isOpen,
      onScroll: updateEmbeddedCommentCoordinates,
      useCapture: true
    };
    $[82] = isOpen;
    $[83] = updateEmbeddedCommentCoordinates;
    $[84] = t26;
  } else {
    t26 = $[84];
  }
  useWindowScrollListener(t26);
  let t27;
  if ($[85] !== isCommentModeActive || $[86] !== prevIsCommentModeActive || $[87] !== toggleOpenPopover) {
    t27 = {
      isCommentModeActive,
      prevIsCommentModeActive,
      toggleOpenPopover
    };
    $[85] = isCommentModeActive;
    $[86] = prevIsCommentModeActive;
    $[87] = toggleOpenPopover;
    $[88] = t27;
  } else {
    t27 = $[88];
  }
  useEmbeddedCommentChangeHandlers(t27);
  let t28;
  if ($[89] !== isOpen || $[90] !== onUpdateCollaborationSidebarState || $[91] !== prevIsOpen) {
    t28 = {
      onUpdateCollaborationSidebarState,
      isOpen,
      prevIsOpen
    };
    $[89] = isOpen;
    $[90] = onUpdateCollaborationSidebarState;
    $[91] = prevIsOpen;
    $[92] = t28;
  } else {
    t28 = $[92];
  }
  useEmbeddedCommentClickListener(t28);
  const t29 = `commentable-area-${moduleId}`;
  let t30;
  if ($[93] !== isCommenting) {
    t30 = classnames(COMMENTABLE_AREA_IDENTIFIER, isCommenting && "commenting-commentable-area");
    $[93] = isCommenting;
    $[94] = t30;
  } else {
    t30 = $[94];
  }
  const t31 = as === "div";
  let t32;
  let t33;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = () => setIsHovering(true);
    t33 = () => setIsHovering(false);
    $[95] = t32;
    $[96] = t33;
  } else {
    t32 = $[95];
    t33 = $[96];
  }
  let t34;
  if ($[97] !== autoOpenCommentPopover || $[98] !== collaborationSidebarState || $[99] !== iconPosition || $[100] !== iconProps || $[101] !== isOpen || $[102] !== isTr || $[103] !== leftOffset || $[104] !== metaDescription || $[105] !== moduleId || $[106] !== moduleParentComment || $[107] !== onClosePopover || $[108] !== onUpdateCollaborationSidebarState || $[109] !== placement || $[110] !== renderCommentIndicator || $[111] !== setIsCommenting || $[112] !== showEmbeddedComment || $[113] !== toggleOpenPopover || $[114] !== topOffset || $[115] !== userInfo || $[116] !== verticalPositioning) {
    t34 = showEmbeddedComment && /*#__PURE__*/_jsx(EmbeddedComment, {
      moduleId: moduleId,
      isOpenFromCommentMode: autoOpenCommentPopover,
      onClose: onClosePopover,
      commentableAreaRef: commentableAreaRef,
      collaborationSidebarState: collaborationSidebarState,
      verticalPositioning: verticalPositioning || isTr,
      popoverPlacement: placement,
      iconPosition: iconPosition,
      iconProps: iconProps,
      renderCommentIndicator: renderCommentIndicator,
      setIsCommenting: setIsCommenting,
      metaDescription: metaDescription,
      moduleParentComment: moduleParentComment,
      userInfo: userInfo,
      onUpdateCollaborationSidebarState: onUpdateCollaborationSidebarState,
      topOffset: topOffset,
      leftOffset: leftOffset,
      isOpen: isOpen,
      embeddedCommentWrapperRef: embeddedCommentWrapperRef,
      toggleOpenPopover: toggleOpenPopover
    });
    $[97] = autoOpenCommentPopover;
    $[98] = collaborationSidebarState;
    $[99] = iconPosition;
    $[100] = iconProps;
    $[101] = isOpen;
    $[102] = isTr;
    $[103] = leftOffset;
    $[104] = metaDescription;
    $[105] = moduleId;
    $[106] = moduleParentComment;
    $[107] = onClosePopover;
    $[108] = onUpdateCollaborationSidebarState;
    $[109] = placement;
    $[110] = renderCommentIndicator;
    $[111] = setIsCommenting;
    $[112] = showEmbeddedComment;
    $[113] = toggleOpenPopover;
    $[114] = topOffset;
    $[115] = userInfo;
    $[116] = verticalPositioning;
    $[117] = t34;
  } else {
    t34 = $[117];
  }
  let t35;
  if ($[118] !== as || $[119] !== buttonA11yProps || $[120] !== children || $[121] !== isCommentModeActive || $[122] !== isCommenting || $[123] !== isHighlightedBySidebar || $[124] !== isTableChild || $[125] !== isTr || $[126] !== onClickHandler || $[127] !== propsForWrapper || $[128] !== showComments || $[129] !== t29 || $[130] !== t30 || $[131] !== t31 || $[132] !== t34) {
    t35 = /*#__PURE__*/_jsxs(StyledHoverableCommentableArea, Object.assign({
      id: t29,
      className: t30,
      isCommentModeActive: isCommentModeActive,
      isCommenting: isCommenting,
      isTableChild: isTableChild,
      isTr: isTr,
      showComments: showComments,
      ref: commentableAreaRef,
      as: as,
      isDefaultElement: t31,
      isHighlightedBySidebar: isHighlightedBySidebar,
      onMouseEnter: t32,
      onMouseLeave: t33
    }, buttonA11yProps, propsForWrapper, {
      onClick: onClickHandler,
      children: [children, t34]
    }));
    $[118] = as;
    $[119] = buttonA11yProps;
    $[120] = children;
    $[121] = isCommentModeActive;
    $[122] = isCommenting;
    $[123] = isHighlightedBySidebar;
    $[124] = isTableChild;
    $[125] = isTr;
    $[126] = onClickHandler;
    $[127] = propsForWrapper;
    $[128] = showComments;
    $[129] = t29;
    $[130] = t30;
    $[131] = t31;
    $[132] = t34;
    $[133] = t35;
  } else {
    t35 = $[133];
  }
  return t35;
}
function _temp() {}
export default EmbeddedCommentableArea;